/**
 * マニュアル（HTML）の表示。
 *
 * 書くのは月次経理の型（FMT）の編集画面、読むのはタスクの詳細。どちらも同じ見た目に
 * なるよう、削り方と余白の指定をここ 1 か所に置く。書き手が編集画面で見たとおりの
 * ものがタスク側にも出る。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business マニュアルの HTML を、危ないタグを落としたうえで読みやすく描く。
 * @graph-connects none
 */
import { sanitizeHtml } from '@/tasks/sanitize-html';

/**
 * 手順書の中の見出しや箇条書きに、最低限の間隔を与える指定。
 *
 * 書く人は HTML だけを置くので、余白は画面側で持つ。Tailwind の素のままだと
 * 見出しも箇条書きも潰れて読めない
 * @graph-connects none
 */
const MANUAL_CLASS = [
  'text-sm leading-relaxed text-[var(--color-text-primary)]',
  '[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-base [&_h1]:font-semibold',
  '[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold',
  '[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-sm [&_h3]:font-semibold',
  '[&_p]:my-2',
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
  '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:my-0.5',
  '[&_a]:text-[var(--color-accent)] [&_a]:underline',
  '[&_table]:my-3 [&_table]:w-full [&_table]:border-collapse',
  '[&_th]:border [&_th]:border-[var(--color-border-subtle)] [&_th]:bg-[var(--color-bg-subtle)] [&_th]:px-2 [&_th]:py-1 [&_th]:text-left',
  '[&_td]:border [&_td]:border-[var(--color-border-subtle)] [&_td]:px-2 [&_td]:py-1',
  '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-[var(--color-bg-subtle)] [&_pre]:p-3',
  '[&_code]:rounded [&_code]:bg-[var(--color-bg-subtle)] [&_code]:px-1 [&_code]:py-0.5',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--color-border-subtle)] [&_blockquote]:pl-3 [&_blockquote]:text-[var(--color-text-secondary)]',
  '[&_hr]:my-4 [&_hr]:border-[var(--color-border-subtle)]',
].join(' ');

/**
 * ManualView の props。
 */
export interface ManualViewProps {
  /** 手順書の中身（HTML） */
  manual: string;
  /** 外側に足す指定。余白やスクロールは置き場所ごとに変わる */
  className?: string;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business マニュアルの HTML を、出してよいタグと属性だけに削って描く
 * @graph-connects sanitizeHtml [calls] 出してよいタグと属性だけに削る
 */
export function ManualView({ manual, className = '' }: ManualViewProps) {
  return (
    // 中身は手で書いた HTML。`sanitizeHtml` で危ないタグと属性を落としてから出す
    <div
      className={`${MANUAL_CLASS} ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(manual) }}
    />
  );
}
